import { mathFormat } from '@sg/utils/common';

/**
 * Helper function to get the correct YoY value based on series name
 */
const getYoYValue = (item: any) => {
  // console.log('itemitemitem',item);
  // console.log('item.seriesName', item.seriesName);
  const seriesName = item.seriesName?.toLowerCase();
  if (seriesName?.includes('studio')) {
    return item.data.yoy?.studio || '0.0%';
  } else if (seriesName?.includes('publishing')) {
    return item.data.yoy?.publishing || '0.0%';
  } else if (seriesName?.includes('casual')) {
    return item.data.yoy?.casual || '0.0%';
  }
  return item.data.yoy?.total || '0.0%';
};

/**
 * Helper function to get the correct MoM value based on series name
 */
const getMoMValue = (item: any) => {
  const seriesName = item.seriesName?.toLowerCase();
  if (seriesName?.includes('studio')) {
    return item.data.mom?.studio || '0.0%';
  } else if (seriesName?.includes('publishing')) {
    return item.data.mom?.publishing || '0.0%';
  } else if (seriesName?.includes('casual')) {
    return item.data.mom?.casual || '0.0%';
  }
  return item.data.mom?.total || '0.0%';
};

/**
 * Factory function to create tooltip renderers for different chart types
 * This replicates the exact same logic as the old commented code
 */
let tooltipCallCount = 0;

export const createOverallProfitTooltipRenderer = (
  index: number,
  translations: { [key: string]: string },
  isForecast: boolean = false,
  breakdownKeys: string[] = ['studio', 'publishing'],
) => {
  return (item?: any) => {
    tooltipCallCount++;

    if (!item) {
      return '<span></span>';
    }

    // If the value is undefined or null, don't render this tooltip item
    if (item.data?.value === undefined || item.data?.value === null) {
      return '<span></span>';
    }

    const dataTypeLabel = isForecast ? 'Forecast Total' : 'Actual Total';
    // Keep tooltip marker aligned with legend dot colors (not bar gradient colors).
    const markerColor = isForecast ? '#D2C8FD' : '#7F6FF1';
    const markerDot = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${markerColor};vertical-align:middle;"></span>`;

    const breakdownRows = breakdownKeys
      .map((key) => {
        const label = translations[key] || key;
        return `<div class="dbs-base-chart-tooltip-item" style="display: flex;justify-content: space-between;margin-top:10px">
        <span class="dbs-base-chart-tooltip-item-name" style="display: inline-block; width: 60px">
          <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>
          <span>${label}</span>
        </span>
        <span class="dbs-base-chart-tooltip-item-value">
          <span style="font-size: 14px;font-weight: 600">${mathFormat(item.data?.[key])}</span>
          ${item.data?.tip || ''}
        </span>
      </div>`;
      })
      .join('');

    return `${item.data?.totalShowContent && index === 0 ? `<div class="dbs-base-chart-tooltip-item" style="display: flex;justify-content: space-between;margin-top:10px">
      <span class="dbs-base-chart-tooltip-item-name">
        <span style="font-size: 13px">Total</span>
      </span>
      <span class="dbs-base-chart-tooltip-item-value">
        <span style="font-size: 14px;font-weight: 600">${item.data.totalShowContent}</span>
      </span>
      </div>` : ''}
      <div class="dbs-base-chart-tooltip-item" style="display: flex;justify-content: space-between;margin-top:10px">
        <span class="dbs-base-chart-tooltip-item-name" style="display: inline-block; width: 60px">
          <span>${markerDot}</span>
          <span>${dataTypeLabel}</span>
        </span>
        <span class="dbs-base-chart-tooltip-item-value">
          <span style="font-size: 14px;font-weight: 600">${mathFormat(item.data.value)}</span>
        </span>
      </div>
      ${breakdownRows}
      `;
  };
};

export const createRevenueTrendTooltipRenderer = (keys: string, index: number, translations: { [key: string]: string }, isForecast: boolean = false) => {
  return (item?: any) => {
    if (!item) return '<span></span>';

    const dataTypeLabel = isForecast ? 'Forecast' : 'Actual';
    // Tooltip markers should keep original colors (no faded forecast tone).
    const iconOpacity = '1';

    return keys !== 'ForeCast' ? `${item.data?.totalShowContent && index === 0 ? `<div class="dbs-base-chart-tooltip-item" style="display: flex;justify-content: space-between;margin-top:10px">
      <span class="dbs-base-chart-tooltip-item-name">
        <span style="font-size: 13px">Total</span>
      </span>
      <span class="dbs-base-chart-tooltip-item-value">
        <span style="font-size: 14px;font-weight: 600">${item.data.totalShowContent}</span>
      </span>
      </div>` : ''}
      ${keys === 'studio' ? `<div class="dbs-base-chart-tooltip-item" style="display: flex;justify-content: space-between;margin-top:10px">
        <span class="dbs-base-chart-tooltip-item-name">
          <span style="display: inline-block; width: 12px; height: 12px; border-radius: 50%; background: conic-gradient(#69D19C 0 33.333%, #5EC8F1 33.333% 66.666%, #7F6FF1 66.666% 100%); opacity: ${iconOpacity}; vertical-align: middle;"></span>
          <span style="margin-left: 4px;">${dataTypeLabel}</span>
        </span>
        <span class="dbs-base-chart-tooltip-item-value">
          <span style="font-size: 14px;font-weight: 600">${mathFormat(item.data.total)}</span>
        </span>
        ${item.data.yoy ? `<span class="dbs-base-chart-tooltip-item-value">
          <span style="font-size: 14px;font-weight: 600">${item.data.yoy?.total}</span>
        </span>
        <span class="dbs-base-chart-tooltip-item-value">
          <span style="font-size: 14px;font-weight: 600">${item.data.mom?.total}</span>
        </span>` : ''}
      </div>` : ''}
      
      <div class="dbs-base-chart-tooltip-item" style="display: flex;justify-content: space-between;margin-top:10px">
        <span class="dbs-base-chart-tooltip-item-name">
          <span style="opacity: ${iconOpacity}">${item.marker}</span>
          <span>${item.seriesName}</span>
        </span>
        <span class="dbs-base-chart-tooltip-item-value">
          <span style="font-size: 14px;font-weight: 600">${mathFormat(item.value)}</span>
        </span>
        ${item.data.yoy ? `<span class="dbs-base-chart-tooltip-item-value">
          <span style="font-size: 14px;font-weight: 600">${getYoYValue(item)}</span>
        </span>
        <span class="dbs-base-chart-tooltip-item-value">
          <span style="font-size: 14px;font-weight: 600">${getMoMValue(item)}</span>
        </span>` : ''}
      </div>
      
      ${keys === 'casual' ? `<div style="height: 1px; width: 100%;background-color: rgba(255, 255, 255, 0.4);margin: 5px 0px 0px 0px"></div>` : ''}
      ` : '';
  };
};
